/**
 * MyProfiles — "Moje profile i oferty".
 *
 * User po stworzeniu rzeczy (hodowla / reproduktor / szczeniaki / firma)
 * tutaj zawsze może wrócić i:
 *   - zobaczyć publiczną stronę,
 *   - edytować dane,
 *   - udostępnić link,
 *   - dodać kolejną rzecz.
 *
 * Sekcje są zależne od user.profile_type:
 *   - hodowca: Hodowla / Reproduktory / Szczenięta
 *   - biznes:  Firma / usługi
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dog, Heart, Award, Building2,
  ExternalLink, Pencil, Share2, Plus, Loader2,
  FolderOpen, Sparkles, MessageCircle, ArrowRight, Eye, Search,
  MoreVertical, EyeOff, Trash2,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import StudioShell from '@/components/studio/StudioShell';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/utils/api';

const FRONTEND_HOST = (typeof window !== 'undefined' ? window.location.origin : '').replace(/^https?:\/\//, '');

const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });


function StatusBadge({ status }) {
  const map = {
    published: { label: 'Online',               tone: 'emerald' },
    draft:     { label: 'Szkic',                tone: 'amber' },
    incomplete:{ label: 'Wymaga uzupełnienia',  tone: 'orange' },
    no_page:   { label: 'Brak strony',          tone: 'zinc' },
  };
  const cfg = map[status] || map.draft;
  const cls = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber:   'bg-amber-50 text-amber-700 ring-amber-200',
    orange:  'bg-orange-50 text-orange-700 ring-orange-200',
    zinc:    'bg-zinc-100 text-zinc-500 ring-zinc-200',
  }[cfg.tone];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full ring-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${cls}`}>
      {cfg.label}
    </span>
  );
}

/* Pasek statystyk u góry — „ile czego mam" + kontakt. Wszystkie liczby realne. */
function StatsBar({ stats }) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3"
      data-testid="my-profiles-stats-bar"
    >
      {stats.map((s) => (
        <div
          key={s.key}
          className="apisai-soft-card px-4 py-3.5 flex items-center gap-3"
          data-testid={`stat-tile-${s.key}`}
        >
          <div
            className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--apisai-primary-50, rgba(249,115,22,0.1))' }}
          >
            <s.Icon className="w-5 h-5 text-[var(--apisai-primary)]" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-[var(--apisai-text-primary)] text-xl font-bold leading-none tracking-tight">
              {s.value}
            </p>
            <p className="text-[var(--apisai-text-muted)] text-[11px] font-medium mt-0.5 truncate">
              {s.label}
            </p>
          </div>
          {s.badge ? (
            <span
              className="ml-auto flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full bg-[var(--apisai-primary)] text-white text-[10px] font-bold"
              data-testid={`stat-tile-${s.key}-badge`}
            >
              {s.badge}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/* Mały badge „W katalogu" obok statusu publikacji */
function CatalogBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 bg-sky-50 text-sky-700 ring-sky-200 text-[10px] font-semibold uppercase tracking-[0.12em]">
      <Search className="w-2.5 h-2.5" strokeWidth={2.5} />
      W katalogu
    </span>
  );
}

/* Polish pluralization for "zapytanie/zapytania/zapytań" + "nowe/nowych" */
const plLeadsNoun = (n) => {
  const m10 = n % 10, m100 = n % 100;
  if (n === 1) return 'zapytanie';
  if (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14)) return 'zapytania';
  return 'zapytań';
};
const newAdj = (n) => {
  const m10 = n % 10, m100 = n % 100;
  return (n === 1 || (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14))) ? 'nowe' : 'nowych';
};

/* Kontekstowy licznik zapytań na karcie (nie nowy moduł CRM — tylko sygnał + link) */
function LeadCounter({ count, onLeads, testId }) {
  if (!count || !count.total) return null;
  const hasNew = count.new > 0;
  return (
    <button
      type="button"
      onClick={onLeads}
      data-testid={testId}
      className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] group"
    >
      <MessageCircle className={`w-3.5 h-3.5 ${hasNew ? 'text-[var(--apisai-primary)]' : 'text-[var(--apisai-text-muted)]'}`} strokeWidth={2} />
      {hasNew ? (
        <span className="font-semibold text-[var(--apisai-primary)]">
          {count.new} {newAdj(count.new)} {plLeadsNoun(count.new)}
        </span>
      ) : (
        <span className="text-[var(--apisai-text-muted)]">
          {count.total} {plLeadsNoun(count.total)} łącznie
        </span>
      )}
      <span className="text-[var(--apisai-text-muted)]">·</span>
      <span className="inline-flex items-center gap-0.5 text-[var(--apisai-text-secondary)] font-medium group-hover:text-[var(--apisai-primary)] transition-colors">
        Zobacz zapytania
        <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </button>
  );
}

function ProfileCard({ item, onEdit, onView, onShare, publicPath, leadCount, onLeads, createPageLabel, onCreatePage, onDelete, onToggleHidden }) {
  const hasPage = !!item.slug;
  const isVisible = item.status === 'published';
  return (
    <div
      className="apisai-soft-card p-4 sm:p-5 flex items-center gap-4 hover:shadow-[var(--polish-shadow-card)] transition-all"
      data-testid={`profile-card-${item.id}`}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-xl overflow-hidden ring-1 ring-black/5 bg-[var(--polish-bg-soft)] flex items-center justify-center">
        {item.thumb ? (
          <img src={item.thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <FolderOpen className="w-7 h-7 text-[var(--apisai-text-muted)]" strokeWidth={1.5} />
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <StatusBadge status={item.status} />
          {item.inCatalog && hasPage && <CatalogBadge />}
          {item.sub && (
            <span className="text-[var(--apisai-text-muted)] text-[12px] truncate">{item.sub}</span>
          )}
        </div>
        <h4 className="text-[var(--apisai-text-primary)] text-base font-semibold tracking-tight truncate">
          {item.name}
        </h4>
        {hasPage ? (
          <p className="text-[var(--apisai-text-muted)] text-[12px] truncate">
            {FRONTEND_HOST}{publicPath}/{item.slug}
          </p>
        ) : (
          <p className="text-[var(--apisai-text-muted)] text-[12px] truncate">
            Strona prezentacji jeszcze nie istnieje
          </p>
        )}
        {/* Statystyki strony: wyświetlenia + zapytania (realne dane) */}
        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          {hasPage && (
            <span className="inline-flex items-center gap-1 text-[12px] text-[var(--apisai-text-muted)]" data-testid={`stat-views-${item.id}`}>
              <Eye className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="font-semibold text-[var(--apisai-text-secondary)]">{item.views || 0}</span> wyśw.
            </span>
          )}
          <LeadCounter count={leadCount} onLeads={onLeads} testId={`lead-counter-${item.id}`} />
        </div>
      </div>

      {/* Actions */}
      <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
        {hasPage ? (
          <>
            <button
              onClick={() => onView(item)}
              data-testid={`profile-action-view-${item.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg ring-1 ring-[var(--polish-border)] text-[var(--apisai-text-primary)] text-[13px] font-medium hover:bg-[var(--polish-bg-soft)] transition-colors"
              title="Zobacz stronę"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Zobacz
            </button>
            <button
              onClick={() => onShare(item)}
              data-testid={`profile-action-share-${item.id}`}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg ring-1 ring-[var(--polish-border)] text-[var(--apisai-text-primary)] hover:bg-[var(--polish-bg-soft)] transition-colors"
              title="Udostępnij"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onEdit(item)}
              data-testid={`profile-action-edit-${item.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-[13px] font-semibold transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edytuj
            </button>
          </>
        ) : (
          <button
            onClick={() => (onCreatePage || onEdit)(item)}
            data-testid={`profile-action-create-page-${item.id}`}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-[13px] font-semibold transition-colors shadow-[0_8px_20px_-6px_rgba(249,115,22,0.5)]"
          >
            <Plus className="w-3.5 h-3.5" />
            {createPageLabel || 'Utwórz stronę prezentacji'}
          </button>
        )}
        {onDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`profile-action-more-${item.id}`}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg ring-1 ring-[var(--polish-border)] text-[var(--apisai-text-primary)] hover:bg-[var(--polish-bg-soft)] transition-colors"
                title="Więcej"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {hasPage && onToggleHidden && (
                <DropdownMenuItem onClick={() => onToggleHidden(item)} data-testid={`profile-action-toggle-${item.id}`}>
                  {isVisible ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {isVisible ? 'Uśpij stronę' : 'Wznów stronę'}
                </DropdownMenuItem>
              )}
              {hasPage && onToggleHidden && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => onDelete(item)} data-testid={`profile-action-delete-${item.id}`} className="text-red-600 focus:text-red-700 focus:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Usuń na zawsze
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Mobile */}
      <div className="flex sm:hidden items-center gap-1.5 flex-shrink-0">
        <button
          onClick={() => (hasPage ? onEdit : (onCreatePage || onEdit))(item)}
          data-testid={`profile-action-edit-${item.id}-mobile`}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--apisai-primary)] text-white"
        >
          {hasPage ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
        {onDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`profile-action-more-${item.id}-mobile`}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg ring-1 ring-[var(--polish-border)] text-[var(--apisai-text-primary)]"
                title="Więcej"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {hasPage && onToggleHidden && (
                <DropdownMenuItem onClick={() => onToggleHidden(item)} data-testid={`profile-action-toggle-${item.id}-mobile`}>
                  {isVisible ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {isVisible ? 'Uśpij stronę' : 'Wznów stronę'}
                </DropdownMenuItem>
              )}
              {hasPage && onToggleHidden && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => onDelete(item)} data-testid={`profile-action-delete-${item.id}-mobile`} className="text-red-600 focus:text-red-700 focus:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Usuń na zawsze
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function EmptySection({ title, desc, ctaLabel, onCta, Icon }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl ring-1 ring-[var(--polish-border)] bg-gradient-to-br from-white via-[var(--polish-bg-soft)] to-white"
      data-testid={`empty-section-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Ambient orange glow */}
      <div
        className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.22), transparent 70%)' }}
        aria-hidden="true"
      />

      <div className="relative grid grid-cols-1 sm:grid-cols-[1fr_300px] gap-6 sm:gap-7 p-5 sm:p-7 items-center">
        {/* Lewa kolumna: copy + CTA */}
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--apisai-primary-700)] mb-2">
            Tak to będzie wyglądać
          </p>
          <h4 className="text-[var(--apisai-text-primary)] text-lg sm:text-xl font-bold leading-tight tracking-tight">
            {title}
          </h4>
          <p className="text-[var(--apisai-text-secondary)] text-sm mt-1.5 leading-relaxed mb-5">
            {desc}
          </p>
          <button
            onClick={onCta}
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-all shadow-[0_10px_24px_-6px_rgba(249,115,22,0.5)] hover:-translate-y-0.5"
            data-testid={`empty-cta-${title?.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Plus className="w-4 h-4" />
            {ctaLabel}
          </button>
        </div>

        {/* Prawa kolumna: większy mock browser preview */}
        <div
          className="relative w-full h-[180px] rounded-2xl overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_-15px_rgba(0,0,0,0.25)] flex flex-col"
          style={{ background: 'linear-gradient(160deg, #04202F 0%, #073447 100%)' }}
          aria-hidden="true"
        >
          {/* Window chrome */}
          <div className="flex items-center gap-1.5 px-3 py-2 bg-black/35 border-b border-white/5">
            <span className="w-2 h-2 rounded-full bg-red-400/70" />
            <span className="w-2 h-2 rounded-full bg-yellow-400/70" />
            <span className="w-2 h-2 rounded-full bg-green-400/70" />
            <div className="flex-1 mx-3 h-1.5 rounded-full bg-white/10" />
          </div>
          {/* Hero area */}
          <div className="relative flex-1 flex items-center justify-center overflow-hidden">
            <div
              className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-50"
              style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.55), transparent 70%)' }}
            />
            <div
              className="absolute -bottom-12 -left-12 w-32 h-32 rounded-full opacity-25"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4), transparent 70%)' }}
            />
            <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center bg-white/8 ring-1 ring-white/15">
              <Icon className="w-6 h-6 text-[var(--apisai-primary)]" strokeWidth={1.5} />
            </div>
          </div>
          {/* Content lines + mock CTA */}
          <div className="px-3 pb-3 space-y-1.5">
            <div className="h-1.5 w-3/4 rounded-full bg-white/35" />
            <div className="h-1 w-1/2 rounded-full bg-white/15" />
            <div className="flex items-center gap-2 pt-1.5">
              <div className="h-5 w-16 rounded-md bg-[var(--apisai-primary)]/85" />
              <div className="h-5 w-12 rounded-md bg-white/10" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, sub, Icon, items, onEdit, onView, onShare, onAdd, emptyCfg, leadCounts, onLeads, createPageLabel, addLabel, onDelete, onToggleHidden }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Icon className="w-4 h-4 text-[var(--apisai-primary)]" strokeWidth={2} />
          <h2 className="text-[var(--apisai-text-primary)] text-base sm:text-lg font-bold tracking-tight">
            {title}
          </h2>
          <span className="text-[var(--apisai-text-muted)] text-[12px] font-medium">· {items.length}</span>
        </div>
        {items.length > 0 && (
          <button
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 text-[var(--apisai-primary)] hover:text-[var(--apisai-primary-600)] text-[13px] font-semibold transition-colors"
            data-testid={`add-more-${title?.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Plus className="w-3.5 h-3.5" />
            {addLabel || 'Dodaj kolejny'}
          </button>
        )}
      </div>
      <p className="text-[var(--apisai-text-secondary)] text-[13px]">{sub}</p>

      {items.length === 0 ? (
        <EmptySection {...emptyCfg} />
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <ProfileCard
              key={it.id}
              item={it}
              onEdit={onEdit}
              onView={onView}
              onShare={onShare}
              publicPath={emptyCfg.publicPath}
              leadCount={leadCounts?.[it.id]}
              onLeads={onLeads}
              createPageLabel={createPageLabel}
              onCreatePage={onEdit}
              onDelete={onDelete}
              onToggleHidden={onToggleHidden}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function MyProfiles() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBiznes = user?.profile_type === 'biznes';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ kennels: [], studs: [], litters: [], businesses: [] });
  const [, setHeroConfig] = useState(null);
  const [leadCounts, setLeadCounts] = useState({});
  const [viewsBySlug, setViewsBySlug] = useState({ kennel: {}, stud: {}, litter: {} });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    const entityTypeQuery = isBiznes ? 'business' : 'kennel';
    try {
      // Hero (top sekcji) — równolegle do listingów
      const heroPromise = axios
        .get(`${API}/hero-creator/draft?entity_type=${entityTypeQuery}`, { headers })
        .catch(() => ({ data: { config: null } }));

      // Zapytania — kontekstowy licznik per encja (read-only, bez zmian logiki leadów)
      axios.get(`${API}/panel/leads/all`, { headers })
        .then((r) => {
          const counts = {};
          (Array.isArray(r.data) ? r.data : []).forEach((ld) => {
            const eid = ld.entity_id;
            if (!eid) return;
            if (!counts[eid]) counts[eid] = { total: 0, new: 0 };
            counts[eid].total += 1;
            if ((ld.status || 'new') === 'new') counts[eid].new += 1;
          });
          setLeadCounts(counts);
        })
        .catch(() => {});

      if (isBiznes) {
        const [hRes, bRes] = await Promise.all([
          heroPromise,
          axios.get(`${API}/business/my`, { headers }).catch(() => ({ data: [] })),
        ]);
        setHeroConfig(hRes.data?.config || null);
        const items = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.items || []);
        setData({ kennels: [], studs: [], litters: [], businesses: items });
      } else {
        const [hRes, kRes, sRes, lRes, vRes] = await Promise.all([
          heroPromise,
          axios.get(`${API}/kennels`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/studs`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/litters`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/stats/my-views`, { headers }).catch(() => ({ data: {} })),
        ]);
        setHeroConfig(hRes.data?.config || null);
        // Mapa wyświetleń per slug (realne dane z page_views_total przez /stats/my-views)
        const mapViews = (g) => Object.fromEntries(((g && g.by_page) || []).map((p) => [p.slug, p.total]));
        const v = vRes.data || {};
        setViewsBySlug({ kennel: mapViews(v.kennels), stud: mapViews(v.studs), litter: mapViews(v.litters) });
        setData({
          kennels: Array.isArray(kRes.data) ? kRes.data : (kRes.data?.items || []),
          studs: Array.isArray(sRes.data) ? sRes.data : (sRes.data?.items || []),
          litters: Array.isArray(lRes.data) ? lRes.data : (lRes.data?.items || []),
          businesses: [],
        });
      }
    } catch (e) {
      console.error('[MyProfiles] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [isBiznes]);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  // Spójne źródło prawdy widoczności: public_status > is_hidden/published > show_in_catalog
  const isLive = (e) => {
    if (e.public_status) return e.public_status === 'published';
    if (e.is_hidden) return false;
    if (e.published === false) return false;
    return e.published === true || e.show_in_catalog !== false;
  };
  const adaptKennel = (k) => ({
    id: k.id, name: k.name, slug: k.slug, thumb: k.logo || k.main_photo,
    sub: k.city || k.voivodeship,
    status: isLive(k) ? 'published' : (k.name ? 'draft' : 'incomplete'),
    views: viewsBySlug.kennel?.[k.slug] || 0,
    inCatalog: isLive(k) && k.show_in_catalog !== false,
  });
  const adaptStud = (s) => ({
    id: s.id, name: s.name, slug: s.slug, thumb: s.main_photo,
    sub: s.breed,
    status: !s.slug ? 'no_page' : (isLive(s) ? 'published' : 'draft'),
    views: viewsBySlug.stud?.[s.slug] || 0,
    inCatalog: isLive(s) && s.show_in_catalog !== false,
  });
  const adaptLitter = (l) => ({
    id: l.id, name: l.name || `Miot ${l.breed || ''}`.trim(), slug: l.slug, thumb: l.main_photo,
    sub: l.breed,
    status: !l.slug ? 'no_page' : (isLive(l) ? 'published' : 'draft'),
    views: viewsBySlug.litter?.[l.slug] || 0,
    inCatalog: isLive(l) && l.show_in_catalog !== false,
  });
  const adaptBusiness = (b) => ({
    id: b.id, name: b.name, slug: b.slug, thumb: b.logo || b.main_photo,
    sub: b.city || b.category,
    status: isLive(b) ? 'published' : (b.name ? 'draft' : 'incomplete'),
    views: b.views_total || b.views || 0,
    inCatalog: isLive(b) && b.show_in_catalog !== false,
  });

  const handleView = (item) => {
    if (!item.slug) return toast.error('Najpierw opublikuj profil');
    window.open(`/reproduktor/${item.slug}`, '_blank');
  };
  const handleViewKennel = (item) => {
    if (!item.slug) return toast.error('Najpierw opublikuj profil');
    window.open(`/hodowla/${item.slug}`, '_blank');
  };
  const handleViewLitter = (item) => {
    if (!item.slug) return toast.error('Najpierw opublikuj profil');
    window.open(`/miot/${item.slug}`, '_blank');
  };
  const handleViewBusiness = (item) => {
    if (!item.slug) return toast.error('Najpierw opublikuj profil');
    window.open(`/firma/${item.slug}`, '_blank');
  };

  const handleShare = async (item, publicPath) => {
    if (!item.slug) return toast.error('Najpierw opublikuj profil');
    const url = `${window.location.origin}${publicPath}/${item.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: item.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link skopiowany do schowka');
      }
    } catch (_e) {
      try { await navigator.clipboard.writeText(url); toast.success('Link skopiowany'); } catch { toast.error('Nie udało się skopiować linku'); }
    }
  };

  const editKennel = (item) => navigate(`/wizard/hodowla-v2/${item.id}`);
  const editStud   = (item) => navigate(`/wizard/reproduktor/${item.id}`);
  const editLitter = (item) => navigate(`/wizard/miot/${item.id}`);
  const editBusiness = (item) => navigate(`/wizard/firma-v2/${item.id}`);

  const DELETE_EP = { kennel: 'kennels', stud: 'studs', litter: 'litters', business: 'business' };
  const handleDelete = async (item, entityType, label) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć "${item.name}"?\n\nTej operacji nie można cofnąć.`)) return;
    try {
      await axios.delete(`${API}/${DELETE_EP[entityType]}/${item.id}`, { headers: getAuthHeaders() });
      toast.success(`${label} usunięto`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nie udało się usunąć');
    }
  };
  const handleToggleHidden = async (item, entityType) => {
    const hidden = item.status === 'published'; // widoczna → uśpij; uśpiona → wznów
    try {
      await axios.patch(`${API}/my/${entityType}/${item.id}/visibility`, { hidden }, { headers: getAuthHeaders() });
      toast.success(hidden ? 'Strona uśpiona — zdjęta z katalogu' : 'Strona wznowiona — znów widoczna');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nie udało się zmienić widoczności');
    }
  };

  // Pasek statystyk — wszystkie liczby realne (counts + leady + wyświetlenia)
  const totalLeads = Object.values(leadCounts).reduce((a, c) => a + (c.total || 0), 0);
  const newLeads = Object.values(leadCounts).reduce((a, c) => a + (c.new || 0), 0);
  const totalBizViews = data.businesses.reduce((a, b) => a + (b.views_total || b.views || 0), 0);
  const statsTiles = isBiznes
    ? [
        { key: 'firmy', Icon: Building2, label: 'Firmy', value: data.businesses.length },
        { key: 'wyswietlenia', Icon: Eye, label: 'Wyświetlenia', value: totalBizViews },
        { key: 'zapytania', Icon: MessageCircle, label: 'Zapytania', value: totalLeads, badge: newLeads > 0 ? `${newLeads} nowe` : null },
      ]
    : [
        { key: 'hodowle', Icon: Building2, label: 'Hodowle', value: data.kennels.length },
        { key: 'reproduktory', Icon: Award, label: 'Reproduktory', value: data.studs.length },
        { key: 'mioty', Icon: Heart, label: 'Mioty', value: data.litters.length },
        { key: 'zapytania', Icon: MessageCircle, label: 'Zapytania', value: totalLeads, badge: newLeads > 0 ? `${newLeads} nowe` : null },
      ];

  return (
    <StudioShell
      mode="workspace"
      contextLabel="Twoje strony"
      activeKey="mystuff"
    >
      <Helmet><title>Twoje strony — APISAI</title></Helmet>

      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-24 lg:pb-8 space-y-10" data-testid="my-profiles-page">
        {/* Header */}
        <header className="space-y-1">
          <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Twoje strony
          </h1>
          <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-base leading-relaxed max-w-2xl">
            Wszystko, co prezentujesz klientom. Możesz wrócić, uzupełnić, opublikować ponownie albo udostępnić link.
          </p>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 text-[var(--apisai-primary)] animate-spin" />
          </div>
        ) : (
          <>
            {/* Pasek statystyk — „ile czego mam" (na samej górze) */}
            <StatsBar stats={statsTiles} />

            {isBiznes ? (
          <Section
            title="Twoje strony"
            sub="Wszystko, co prezentujesz klientom."
            Icon={Building2}
            items={data.businesses.map(adaptBusiness)}
            onEdit={editBusiness}
            onView={handleViewBusiness}
            onShare={(it) => handleShare(it, '/firma')}
            onDelete={(it) => handleDelete(it, 'business', 'Firmę')}
            onToggleHidden={(it) => handleToggleHidden(it, 'business')}
            onAdd={() => navigate('/wizard/firma-v2')}
            leadCounts={leadCounts}
            onLeads={() => navigate('/panel/leady')}
            createPageLabel="Utwórz stronę firmy"
            addLabel="Dodaj firmę"
            emptyCfg={{
              title: 'Tu pojawi się Twoja strona',
              desc: 'Stwórz ją w kilka minut — nazwa, opis, lokalizacja. Resztę dodasz później.',
              ctaLabel: 'Stwórz pierwszą stronę',
              onCta: () => navigate('/wizard/firma-v2'),
              Icon: Building2,
              publicPath: '/firma',
            }}
          />
        ) : (
          <>
            <Section
              title="Twoja hodowla"
              sub="Główny profil hodowli — pełna prezentacja."
              Icon={Dog}
              items={data.kennels.map(adaptKennel)}
              onEdit={editKennel}
              onView={handleViewKennel}
              onShare={(it) => handleShare(it, '/hodowla')}
              onDelete={(it) => handleDelete(it, 'kennel', 'Hodowlę')}
              onToggleHidden={(it) => handleToggleHidden(it, 'kennel')}
              onAdd={() => navigate('/wizard/hodowla-v2')}
              leadCounts={leadCounts}
              onLeads={() => navigate('/panel/leady')}
              createPageLabel="Utwórz stronę hodowli"
              addLabel="Dodaj stronę hodowli"
              emptyCfg={{
                title: 'Tu pojawi się Twoja hodowla',
                desc: 'Możesz pominąć ten krok i zacząć od reproduktora albo miotu.',
                ctaLabel: 'Stwórz hodowlę',
                onCta: () => navigate('/wizard/hodowla-v2'),
                Icon: Dog,
                publicPath: '/hodowla',
              }}
            />
            <Section
              title="Twoi reproduktorzy"
              sub="Psy do krycia — osiągnięcia, rodowód, kontakt."
              Icon={Award}
              items={data.studs.map(adaptStud)}
              onEdit={editStud}
              onView={handleView}
              onShare={(it) => handleShare(it, '/reproduktor')}
              onDelete={(it) => handleDelete(it, 'stud', 'Reproduktora')}
              onToggleHidden={(it) => handleToggleHidden(it, 'stud')}
              onAdd={() => navigate('/wizard/reproduktor')}
              leadCounts={leadCounts}
              onLeads={() => navigate('/panel/leady')}
              createPageLabel="Utwórz stronę prezentacji reproduktora"
              addLabel="Dodaj reproduktora"
              emptyCfg={{
                title: 'Tu pojawią się Twoi reproduktorzy',
                desc: 'Zacznij od jednego psa — imię, zdjęcia, rodowód i kontakt.',
                ctaLabel: 'Dodaj pierwszego reproduktora',
                onCta: () => navigate('/wizard/reproduktor'),
                Icon: Award,
                publicPath: '/reproduktor',
              }}
            />
            <Section
              title="Twoje mioty"
              sub="Aktualne i planowane — szczeniaki do rezerwacji."
              Icon={Heart}
              items={data.litters.map(adaptLitter)}
              onEdit={editLitter}
              onView={handleViewLitter}
              onShare={(it) => handleShare(it, '/miot')}
              onDelete={(it) => handleDelete(it, 'litter', 'Miot')}
              onToggleHidden={(it) => handleToggleHidden(it, 'litter')}
              onAdd={() => navigate('/wizard/miot')}
              leadCounts={leadCounts}
              onLeads={() => navigate('/panel/leady')}
              createPageLabel="Utwórz stronę prezentacji miotu"
              addLabel="Dodaj miot"
              emptyCfg={{
                title: 'Tu pojawią się Twoje mioty',
                desc: 'Pokaż klientom aktualny miot — zdjęcia, opis, rodzice.',
                ctaLabel: 'Dodaj pierwszy miot',
                onCta: () => navigate('/wizard/miot'),
                Icon: Heart,
                publicPath: '/miot',
              }}
            />
          </>
        )}
          </>
        )}

        {/* Reassurance */}
        <p className="text-center text-[var(--apisai-text-muted)] text-[12px] pt-2">
          Możesz wrócić tutaj w każdej chwili — to Twoja przestrzeń.
        </p>
      </main>
    </StudioShell>
  );
}
